import { Injectable } from '@angular/core';

import {
	CreateChatKnowledgeRequest,
	EvaluateChatKnowledgeRequest,
	UpdateChatKnowledgeRequest,
} from '@reach/interfaces/chat-knowledge/chat-knowledge.requests';
import {
	AskChatKnowledgeResponse,
	CreateChatKnowledgeChatResponse,
	DeleteChatKnowledgeChatHistoryResponse,
	DeleteChatKnowledgeChatResponse,
	GetChatKnowledgeChatsResponse,
	GetChatKnowledgeMessagesResponse,
	UpdateChatKnowledgeChatResponse,
} from '@reach/interfaces/chat-knowledge/chat-knowledge.responses';
import {
	ChatKnowledgeConversationInterface,
	ChatKnowledgeConversationOriginEnum,
} from '@reach/interfaces/chat-knowledge/chat-knowledge-conversation.type';
import { ChatKnowledgeMessageInterface } from '@reach/interfaces/chat-knowledge/chat-knowledge-message.type';
import {
	OrderByEnum,
	RequestPaginationInterface,
} from '@reach/interfaces/requests/request-pagination.interface';

import { BaseApiService } from '~app-client/api/_internal';

@Injectable({ providedIn: 'root' })
export class ChatAiApiService extends BaseApiService {
	protected commonPath = ['chat-knowledge'];

	public async getMessagesByQuestionIds(
		chatId: string,
		questionIds: string[]
	): Promise<GetChatKnowledgeMessagesResponse> {
		const params = {
			messageIds: questionIds,
			t: this.paramTime,
		};
		return await this.http.get<GetChatKnowledgeMessagesResponse>(
			this.url(chatId, 'by-ids'),
			params
		);
	}

	public async ask(chatId: string, question: string): Promise<AskChatKnowledgeResponse> {
		const body = {
			question,
		};
		this.cleanCache();
		return await this.http.post<AskChatKnowledgeResponse>(this.url(chatId, 'ask'), body);
	}

	public async load(
		chatId: string,
		options: Required<
			Pick<
				RequestPaginationInterface<ChatKnowledgeMessageInterface, 'timestampQuestion'>,
				'size'
			>
		> &
			Pick<
				RequestPaginationInterface<ChatKnowledgeMessageInterface, 'timestampQuestion'>,
				'cursor'
			>
	): Promise<GetChatKnowledgeMessagesResponse> {
		const params = {
			size: `${options.size}`,
			cursor: `${options.cursor}`,
			orderBy: OrderByEnum.DESC,
			t: `${this.cacheTimer}`,
		};
		return await this.http.get<GetChatKnowledgeMessagesResponse>(
			this.url(chatId, 'messages'),
			params
		);
	}

	public async clearChat(chatId: string): Promise<DeleteChatKnowledgeChatHistoryResponse> {
		this.cleanCache();
		return await this.http.delete<DeleteChatKnowledgeChatHistoryResponse>(
			this.url(chatId, 'history')
		);
	}

	public async deleteChat(chatId: string): Promise<DeleteChatKnowledgeChatResponse> {
		this.cleanCache();
		return await this.http.delete<DeleteChatKnowledgeChatResponse>(this.url(chatId, '/'));
	}

	public async likeAnswer(chatId: string, answerId: string): Promise<void> {
		const body: EvaluateChatKnowledgeRequest = {
			rating: 1,
			messageId: answerId,
		};
		return await this.http.post<void>(this.url(chatId, 'feedback'), body);
	}

	public async dislikeAnswer(chatId: string, answerId: string): Promise<void> {
		const body: EvaluateChatKnowledgeRequest = {
			rating: 0,
			messageId: answerId,
		};
		return await this.http.post<void>(this.url(chatId, 'feedback'), body);
	}

	public async removeReaction(chatId: string, answerId: string): Promise<void> {
		const body: EvaluateChatKnowledgeRequest = {
			rating: null,
			messageId: answerId,
		};
		return await this.http.post<void>(this.url(chatId, 'feedback'), body);
	}

	public async getChats(
		origins: ChatKnowledgeConversationOriginEnum[]
	): Promise<GetChatKnowledgeChatsResponse> {
		const params = {
			origins,
		};
		return await this.http.get<GetChatKnowledgeChatsResponse>(this.url('/'), params);
	}

	public async createChat(
		request: CreateChatKnowledgeRequest
	): Promise<ChatKnowledgeConversationInterface> {
		this.cleanCache();
		const response = await this.http.post<CreateChatKnowledgeChatResponse>(
			this.url('/'),
			request
		);
		return response.chat;
	}

	public async editChat(
		chatId: string,
		request: UpdateChatKnowledgeRequest
	): Promise<UpdateChatKnowledgeChatResponse> {
		this.cleanCache();
		return await this.http.patch<UpdateChatKnowledgeChatResponse>(
			this.url(chatId, '/'),
			request
		);
	}
}
